import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { scooterApi, formatPrice, getStatusDotColor } from "@/lib/api";
import { ScooterCard } from "@/components/ScooterCard";
import { SalesStatsWidget, ServiceAlertsWidget } from "@/components/DashboardWidgets";
import { ViewStatsWidget } from "@/components/ViewStatsWidget";
import { UserManagement } from "@/components/UserManagement";
import { ImportScooterDialog } from "@/components/ImportScooterDialog";
import { useAuth } from "@/context/AuthContext";
import { 
  Plus, 
  Package, 
  QrCode,
  TrendingUp,
  Wrench,
  CheckCircle,
  Clock,
  Share2,
  Copy,
  ExternalLink,
  Shield,
  Link2,
  Tag,
  Camera,
  UserPlus,
  Gavel
} from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentScooters, setRecentScooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();

  const showroomUrl = `${window.location.origin}/showroom`;
  const boutiqueShareUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/boutique`;
  const boutiqueUrl = `${window.location.origin}/boutique`;

  const copyShowroomLink = () => {
    navigator.clipboard.writeText(showroomUrl);
    toast.success("Lien vitrine copié! / Showroom link copied!");
  };

  const copyBoutiqueLink = () => {
    navigator.clipboard.writeText(boutiqueShareUrl);
    toast.success("Lien boutique copié! (optimisé Facebook)");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, scootersData] = await Promise.all([
        scooterApi.getStats(),
        scooterApi.getAll()
      ]);
      setStats(statsData);
      setRecentScooters(scootersData.slice(0, 4));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: "Total Stock", 
      value: stats?.total || 0, 
      icon: Package, 
      color: "text-foreground",
      bgColor: "bg-secondary"
    },
    { 
      label: "In Stock / În stoc", 
      value: stats?.in_stock || 0, 
      icon: CheckCircle, 
      color: "text-status-instock",
      bgColor: "bg-status-instock/10"
    },
    { 
      label: "Reserved / Rezervat", 
      value: stats?.reserved || 0, 
      icon: Clock, 
      color: "text-status-reserved",
      bgColor: "bg-status-reserved/10"
    },
    { 
      label: "In Service", 
      value: stats?.in_service || 0, 
      icon: Wrench, 
      color: "text-status-inservice",
      bgColor: "bg-status-inservice/10"
    },
    { 
      label: "Sold / Vândut", 
      value: stats?.sold || 0, 
      icon: TrendingUp, 
      color: "text-status-sold",
      bgColor: "bg-status-sold/10"
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-3xl md:text-4xl">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your scooter inventory professionally and securely.
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-muted-foreground text-sm">
                Conectat ca: <span className="font-medium text-foreground">{user?.username}</span>
              </p>
              {isAdmin() && (
                <span className="flex items-center gap-1 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                  <Shield className="w-3 h-3" />
                  Admin
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/add-scooter">
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
                data-testid="btn-add-scooter"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Scooter
              </Button>
            </Link>
            {isAdmin() && (
              <ImportScooterDialog 
                onImportSuccess={(data) => {
                  fetchData();
                  toast.success(`Scooter ${data.scooter_id} importé!`);
                }}
              />
            )}
            <Link to="/stock">
              <Button variant="outline" data-testid="btn-view-stock">
                <Package className="w-4 h-4 mr-2" />
                Vezi stocul
              </Button>
            </Link>
            <Link to="/labels">
              <Button variant="outline" data-testid="btn-labels">
                <Tag className="w-4 h-4 mr-2" />
                Étiquettes
              </Button>
            </Link>
            <Link to="/photographer">
              <Button variant="outline" data-testid="btn-photographer">
                <Camera className="w-4 h-4 mr-2" />
                Fotografii
              </Button>
            </Link>
            {isAdmin() && (
              <Button variant="outline" data-testid="btn-create-account" onClick={async () => {
                const role = prompt("Rol (photographer, mechanic, organizator, livrator):", "livrator");
                if (!role) return;
                const username = prompt("Nume utilizator:");
                if (!username) return;
                const password = prompt("Parola:");
                if (!password) return;
                try {
                  const token = localStorage.getItem("token");
                  await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/create-user`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ username, password, role })
                  });
                  toast.success(`Cont "${username}" (${role}) creat!`);
                } catch { toast.error("Eroare la crearea contului"); }
              }}>
                <UserPlus className="w-4 h-4 mr-2" />
                + Cont angajat
              </Button>
            )}
            {isAdmin() && (
              <Button className="bg-red-600 hover:bg-red-700" data-testid="btn-announce-auction" onClick={async () => {
                const msg = prompt("Mesaj pentru clienti:", "Licitatia a inceput! Veniti sa votati pe scutersh.store");
                if (!msg) return;
                try {
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/announce-auction`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ message: msg })
                  });
                  const data = await res.json();
                  toast.success(data.message);
                  // Open WhatsApp with pre-written message
                  if (data.whatsapp_message) {
                    window.open(`https://wa.me/?text=${encodeURIComponent(data.whatsapp_message)}`, "_blank");
                  }
                } catch { toast.error("Eroare"); }
              }}>
                <Gavel className="w-4 h-4 mr-2" />
                Anunta Licitatia LIVE
              </Button>
            )}
            {isAdmin() && (
              <Button variant="outline" className="border-gray-500/30" data-testid="btn-stop-banner" onClick={async () => {
                try {
                  const token = localStorage.getItem("token");
                  await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/stop-auction-banner`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success("Banner LIVE dezactivat");
                } catch { toast.error("Eroare"); }
              }}>
                Opreste Banner LIVE
              </Button>
            )}
          </div>
        </div>

        {/* Showroom Link Card */}
        <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Vitrine Client / Client Showroom</h3>
                  <p className="text-sm text-muted-foreground">
                    Partagez ce lien avec vos clients pour voir les scooters disponibles
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyShowroomLink}
                  className="flex-1 sm:flex-none"
                  data-testid="btn-copy-showroom"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier le lien
                </Button>
                <a href="/showroom" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="btn-open-showroom"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Boutique Link Card */}
        <Card className="border-border border-emerald-500/30 bg-emerald-500/5" data-testid="boutique-link-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <Share2 className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Boutique / Magazin Online</h3>
                  <p className="text-sm text-muted-foreground">
                    Lien optimisé pour Facebook - partage avec photo professionnelle
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyBoutiqueLink}
                  className="flex-1 sm:flex-none border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="btn-copy-boutique"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier lien Facebook
                </Button>
                <a href="/boutique" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="btn-open-boutique"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((stat) => (
            <Card 
              key={stat.label} 
              className={`${stat.bgColor} border-border hover:border-primary/30 transition-colors`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  <div>
                    <p className={`font-mono font-bold text-2xl ${stat.color}`}>
                      {stat.value}
                    </p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stock Value - Admin Only */}
        {isAdmin() && (
          <Card className="border-border bg-gradient-to-r from-card to-card/50">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">
                    Total Stock Value / Valeur totale du stock
                  </p>
                  <p className="font-mono font-bold text-4xl text-primary">
                    {formatPrice(stats?.total_value || 0)}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusDotColor("in_stock") }} />
                    <span>In Stock</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusDotColor("reserved") }} />
                    <span>Reserved</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusDotColor("in_service") }} />
                    <span>In Service</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sales Stats, Service Alerts, and View Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {isAdmin() && <SalesStatsWidget />}
          <ServiceAlertsWidget />
          <ViewStatsWidget />
        </div>

        {/* User Management - Admin Only */}
        {isAdmin() && <UserManagement />}

        {/* Recent Scooters */}
        {recentScooters.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl">
                Recent Additions / Adăugări recente
              </h2>
              <Link to="/stock" className="text-sm text-primary hover:underline">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentScooters.map((scooter) => (
                <ScooterCard key={scooter.id} scooter={scooter} />
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/add-scooter">
            <Card className="border-border hover:border-primary/50 transition-colors cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Plus className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Add New Scooter</h3>
                  <p className="text-sm text-muted-foreground">Adaugă scooter nou</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/stock">
            <Card className="border-border hover:border-primary/50 transition-colors cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Vezi stocul</h3>
                  <p className="text-sm text-muted-foreground">Vezi stocul complet</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/stock">
            <Card className="border-border hover:border-primary/50 transition-colors cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <QrCode className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Search by QR</h3>
                  <p className="text-sm text-muted-foreground">Caută cu QR code</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
